import * as THREE from 'three';

/**
 * 画面外ターゲットの方向矢印(ゲームのオフスクリーンインジケータ)。
 * 対象が視野内にあるときは非表示、視野外(後方含む)のときは画面端に
 * 「➤ ラベル」を対象方向へ向けて表示する。
 * 「B-29がどこにいるか分からない=消えた」と誤認される事故を防ぐ。
 */
export class TargetIndicator {
  private els: { root: HTMLElement; arrow: HTMLElement; label: HTMLElement }[] = [];
  private worldPos = new THREE.Vector3();
  private worldScale = new THREE.Vector3();
  private camPos = new THREE.Vector3();
  private camQuat = new THREE.Quaternion();
  private forward = new THREE.Vector3();
  private toTarget = new THREE.Vector3();

  constructor(
    private container: HTMLElement,
    private targets: { label: string; object: THREE.Object3D }[],
  ) {
    for (const t of targets) {
      const root = document.createElement('div');
      root.className = 'target-indicator';
      const arrow = document.createElement('span');
      arrow.className = 'arrow';
      arrow.textContent = '➤';
      const label = document.createElement('span');
      label.textContent = t.label;
      root.append(arrow, label);
      root.style.display = 'none';
      container.appendChild(root);
      this.els.push({ root, arrow, label });
    }
  }

  /** 毎フレーム呼ぶ(provider.update後、render前後どちらでも可) */
  update(camera: THREE.Camera): void {
    if (this.targets.length === 0) return;
    camera.getWorldPosition(this.camPos);
    camera.getWorldQuaternion(this.camQuat);
    this.forward.set(0, 0, -1).applyQuaternion(this.camQuat);
    const W = this.container.clientWidth;
    const H = this.container.clientHeight;

    for (let i = 0; i < this.targets.length; i++) {
      const target = this.targets[i];
      const el = this.els[i];
      if (!target || !el) continue;
      const { object, label } = target;
      object.getWorldScale(this.worldScale);
      // 非表示(スケール0)の対象は矢印も出さない(着弾後の爆弾など)
      if (!object.visible || this.worldScale.x < 0.01) {
        el.root.style.display = 'none';
        continue;
      }
      object.getWorldPosition(this.worldPos);
      this.toTarget.copy(this.worldPos).sub(this.camPos);
      const behind = this.toTarget.dot(this.forward) < 0;
      this.worldPos.project(camera);
      let x = this.worldPos.x;
      let y = this.worldPos.y;
      if (behind) {
        x = -x;
        y = -y;
      }
      const onScreen = !behind && Math.abs(x) < 0.9 && Math.abs(y) < 0.82;
      if (onScreen) {
        el.root.style.display = 'none';
        continue;
      }
      // 画面端(内側マージン付き)へ射影
      const m = Math.max(Math.abs(x), Math.abs(y), 1e-4);
      x = (x / m) * 0.86;
      y = (y / m) * 0.78;
      const px = (x * 0.5 + 0.5) * W;
      const py = (1 - (y * 0.5 + 0.5)) * H;
      const angDeg = (Math.atan2(-y, x) * 180) / Math.PI; // 画面座標系(y下向き)
      // 距離をライブ表示: 「遠くにいる(=消えたのではない)」ことを常に示す
      const dist = this.toTarget.length();
      const distText = dist >= 1000 ? `${(dist / 1000).toFixed(1)}km` : `${dist.toFixed(0)}m`;
      el.label.textContent = `${label} ${distText}`;
      el.root.style.display = 'flex';
      el.root.style.transform = `translate(${px}px, ${py}px) translate(-50%, -50%)`;
      el.arrow.style.transform = `rotate(${angDeg}deg)`;
    }
  }

  destroy(): void {
    for (const el of this.els) el.root.remove();
    this.els = [];
  }
}
